<template>
    <div class="relative isolate overflow-hidden">
        <div class="absolute inset-x-0 -top-24 -z-10 flex justify-center blur-3xl" aria-hidden="true">
            <div class="aspect-[1155/678] w-[50rem] bg-gradient-to-tr from-primary-200 via-primary-100 to-white opacity-70"
                style="clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)" />
        </div>

        <div class="mx-auto max-w-7xl px-6 py-20 sm:py-28 lg:flex lg:items-center lg:gap-x-12 lg:px-8">
            <div class="mx-auto max-w-2xl lg:mx-0 lg:w-3/5 lg:shrink-0">
                <span
                    class="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-sm font-semibold text-primary-700 ring-1 ring-inset ring-primary-600/10">
                    {{ data.subtitle }}
                </span>
                <h1 class="mt-6 text-4xl font-semibold tracking-tight text-gray-900 sm:text-6xl">{{ data.title }}</h1>
                <p class="mt-6 text-lg/8 text-gray-600">
                    {{ data.description }}
                </p>
                <div class="mt-10 flex flex-wrap items-center gap-4">
                    <Button variant="primary" size="lg" @click="bookDemoModal = true">
                        {{ data.buttonText }}
                    </Button>

                    <Dialog v-model:visible="bookDemoModal" modal header="Request a Demo" :style="{ width: '30rem' }" class="mx-4">
                        <div class="flex items-center gap-4 mb-4">
                            <label for="email" class="font-semibold w-24">email</label>
                            <InputText v-model="email" id="email" class="flex-auto" autocomplete="on" />
                        </div>
                        <div class="flex justify-end gap-2">
                            <PvButton type="button" label="Cancel" severity="secondary"
                                @click="bookDemoModal = false"></PvButton>
                            <PvButton type="button" label="Request" @click="requestDemo"></PvButton>
                        </div>
                    </Dialog>
                    <VideoButton :video-url="heroVideoUrl" />
                </div>

                <dl class="mt-16 grid grid-cols-3 gap-x-6 border-t border-gray-100 pt-8">
                    <div v-for="trust in trustPoints" :key="trust.label">
                        <dt class="text-sm text-gray-500">{{ trust.label }}</dt>
                        <dd class="mt-1 text-base font-semibold text-gray-900">{{ trust.value }}</dd>
                    </div>
                </dl>
            </div>

            <div class="mx-auto mt-16 max-w-md lg:mx-0 lg:mt-0 lg:w-2/5 lg:flex-shrink-0">
                <div class="relative mx-auto max-w-md">
                    <div class="absolute -inset-8 -z-10 rounded-full bg-gradient-to-br from-primary-100 via-primary-50 to-accent-100 blur-2xl" aria-hidden="true" />
                    <div class="absolute -top-6 -right-6 -z-10 size-28 rounded-full bg-accent-300/40 blur-2xl" aria-hidden="true" />
                    <img src="/images/home/header.jpg" :alt="image.alt"
                        class="aspect-[4/5] w-full rounded-full object-cover shadow-2xl shadow-primary-900/10 ring-1 ring-accent-200/60" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps } from 'vue';
import VideoButton from '@/components/buttons/VideoButton.vue';
import Button from '@/components/ui/Button.vue';
import PvButton from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import { requestDemoService } from '@/services/requestDemoService';
import { getHomeSettings } from '@/services/homeSettingService';
import { onMounted, ref } from 'vue';

const props = defineProps({
    title: { type: String, default: 'Learn Arabic & the Qur\'an with Native Tutors' },
    image: { type: String, default: '' },
});
const bookDemoModal = ref(false);
const email = ref('');
const heroVideoUrl = ref('');

onMounted(() => {
    getHomeSettings()
        .then((data) => {
            heroVideoUrl.value = data.data?.hero_video_url || '';
        })
        .catch(() => {
            // no hero video configured yet
        });
});

const data = {
    subtitle: 'Welcome to Global Arabic',
    title: props.title,
    description: 'Join us and experience an enriching journey through our online Quran, Arabic, and Islamic studies programs. Our Native Arabic tutors proficient in English.',
    buttonText: 'Book a Free Demo',
};

const image = {
    alt: 'Image of a person studying Quran - Photo by Furkan Dil'
};

const trustPoints = [
    { label: 'Tutors', value: 'Native & Certified' },
    { label: 'Scheduling', value: 'Fully Flexible' },
    { label: 'Support', value: '24/7 Available' },
];

const requestDemo = () => {
    requestDemoService({ email: email.value })
        .then(() => {
            bookDemoModal.value = false;
        })
        .catch(() => {
            alert('There was an error processing your request. Please try again later.');
        });
};
</script>
